import useDeepCompareEffect from 'use-deep-compare-effect'
import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import React, { useRef, useContext, useEffect, useState } from 'react'
import { invalidateCache, useStatePersist } from 'use-state-persist'
import { GetTokenAPIResponse } from '../interfaces/Form'
import DynamicFormContext from './DynamicFormContext'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { useDebouncedEffect } from '../hooks'
import isEqual from 'react-fast-compare'

interface DynamicFormProviderProps {
  children: React.ReactNode
  apiURI: string
  sid: string
  appToken: string
  autoSaveInterval: number
  useFormsAPI: boolean
  dispatch: (eventName: string) => void
  submitFormHandler: () => void
}

interface DynamicFormField {
  field_name: string
  field_value: string
  uid: string
  sid: string
}

const DynamicFormProvider: React.FC<DynamicFormProviderProps> = ({
  children,
  apiURI,
  sid,
  appToken,
  autoSaveInterval,
  useFormsAPI,
  dispatch,
  submitFormHandler
}) => {
  // TODO: have DynamicFormField component access context data for formData using <DynamicFormContext.Consumer>
  const [formData, setFormData] = useState({} as any)
  const [hasFormData, setHasFormData] = useState(!useFormsAPI)

  let formDataRef = useRef(formData)
  const [formDataUIDs, setFormDataUIDs] = useState({} as any)
  const [authToken, setAuthToken] = useState('')
  const [hasSession, setHasSession] = useState(false)
  const [saved, setSaved] = useState(true)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formSubmitSuccess, setformSubmitSuccess] = useState(false)

  /**
   * Update a single form field
   *
   * @param uid The UID for which to update associated field data
   */
  const updateUID = (uid: string, name: string, value: string) => {
    console.log('Updating form field: ', uid)
    return axios.put(
      `${apiURI}/formdata/${uid}`,
      {
        field_name: name,
        field_value: value
      },
      getRequestConfig()
    )
  }

  /**
   * Delete a single form field
   *
   * @param uid The UID for which to delete associated field data
   */
  const deleteUID = (uid: string) => {
    console.log('Deleting form field: ', uid)
    return axios.delete(`${apiURI}/formdata/${uid}`, getRequestConfig())
  }

  /**
   * Get a single form field
   *
   * @param uid The UID to get field data for
   */
  const getUID = (uid: string) => {
    console.log('Getting form field: ', uid)
    return axios.get(`${apiURI}/formdata/${uid}`, getRequestConfig())
  }

  /**
   * Get all fields for the current form
   */
  const getUIDs = () => {
    console.log('Getting all form fields')
    return axios.get(`${apiURI}/formdata`, getRequestConfig())
  }

  /**
   * Create a single form field
   *
   * @param name
   * @param value
   */
  const createUID = (name: string, value: string) => {
    console.log('Storing field data: ', name, value)
    return axios.post(
      `${apiURI}/formdata`,
      {
        field_name: name,
        field_value: value
      },
      getRequestConfig()
    )
  }

  /**
   * Creates a new form session
   */
  const createFormSession = async () => {
    try {
      const config: AxiosRequestConfig = {
        headers: {}
      }

      const sessionRes = await axios.post(
        `${apiURI}/session`,
        {
          sid,
          token: appToken
        },
        config
      )

      setHasSession(true)

      console.log('Created form session for SID: ', sid)
    } catch (error) {
      // Server error
      console.error(error)
    }
  }

  /**
   * Creates an auth token for use on API calls that must be authenticated
   */
  const createAuthToken = async () => {
    try {
      const config: AxiosRequestConfig = {
        headers: {}
      }
      const sessionRes: AxiosResponse = await axios.post(
        `${apiURI}/gettoken`,
        {
          sid,
          token: appToken
        },
        config
      )
      const token = (sessionRes.data as GetTokenAPIResponse).token
      //console.log('Got token: ', token)
      setAuthToken(token)
    } catch (error) {
      // Server error
      console.error(error)
      //dispatch({ type: 'tokenError', message: error.message })
    }
  }

  /**
   * Generate request configs for querying Forms API
   *
   * @returns AxiosRequestConfig object containing auth header
   */
  const getRequestConfig = () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: authToken,
        ContentType: 'application/json'
      }
    }

    return config
  }

  /**
   * Push form data from local state to forms API
   *
   * Stores reference to resulting UIDs locally for future field updates and throttles
   * API calls
   */
  const pushFormData = async () => {
    const formDataOld = formDataRef.current

    if (!(hasSession && authToken)) {
      return
    }

    const promises: AxiosPromise[] = []

    //console.log(formData)
    Object.keys(formData).forEach(async (name, index) => {
      const value = formData[name]
      //console.log(value)
      if (value === formDataOld[name]) {
        // Value hasn't changed - doesn't need to sync
        return
      }
      if (formDataUIDs[name]) {
        promises.push(updateUID(formDataUIDs[name], name, value))
      } else {
        const result = await createUID(name, value)
        setFormDataUIDs({ ...formDataUIDs, [name]: result.data })
      }
    })

    await Promise.all(promises)

    setSaved(true)

    // Store changed form data for later comparison
    formDataRef.current = formData
  }

  /**
   * Pull form data from forms API to local state
   */
  const pullFormData = async () => {
    const result = await getUIDs()
    let data: any = {}
    let uidData: any = {}

    console.log('Got form field data: ', result.data)
    result.data.forEach((field: DynamicFormField) => {
      data[field.field_name] = field.field_value
      uidData[field.field_name] = field.uid
    })

    // Store changed form data for later comparison
    formDataRef.current = data

    setFormData(data)
    setFormDataUIDs(uidData)
    setHasFormData(true)
  }

  const submitForm = async (additionalData: any) => {
    console.log('Submitting form with data: ', additionalData)

    try {
      const result = await axios.post(
        `${apiURI}/submit`,
        {
          data: JSON.stringify(additionalData)
        },
        getRequestConfig()
      )
      //console.log('Result: ', result)

      submitFormHandler()
      setformSubmitSuccess(true)
    } catch (error) {
      console.log(
        'Error submitting form data: ',
        (error as AxiosError).response
      )
      setFormSubmitted(false)
    }
  }

  useDebouncedEffect(() => pushFormData(), autoSaveInterval, [
    formData,
    formDataRef.current,
    hasSession,
    authToken,
    hasFormData
  ])

  useEffect(() => {
    if (hasFormData && !isEqual(formData, formDataRef.current)) {
      setSaved(false)
    }
  }, [formData, formDataRef.current, hasFormData])

  const context = {
    authToken,
    formData,
    setFormData,
    updateUID,
    deleteUID,
    getUID,
    getUIDs,
    createUID,
    saved,
    hasFormData,
    submitForm,
    formSubmitted,
    setFormSubmitted,
    formSubmitSuccess,
    setformSubmitSuccess
  }
  // init
  useEffect(() => {
    if (!useFormsAPI || apiURI === '') {
      return
    }
    //console.log('Forms data: ', formData)
    // Fetch token
    if (hasSession && authToken && !hasFormData) {
      console.log('Filling forms data from API')
      pullFormData()
    } else if (hasSession && !authToken) {
      console.log('Getting forms token')
      // Get token
      createAuthToken()
    } else if (!hasSession && sid) {
      // Forms session doesn't exist yet, create it
      console.log('Creating forms session')
      createFormSession()
    }

    // Return cleanup
    return () => {
      // Do cleanup actions here e.g. invalidate API access token
    }
  }, [sid, appToken, authToken, hasSession])

  return (
    <DynamicFormContext.Provider value={context}>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        {children}
      </MuiPickersUtilsProvider>
    </DynamicFormContext.Provider>
  )
}

export default DynamicFormProvider
